
import { Terminal, RefreshCw } from "lucide-react";

interface TelemetryTerminalPillarProps {
  isProbing: boolean;
  probeResult: string | null;
  showTelemetryTerminal: boolean;
  terminalLogs: string[];
  onProbeGateway: () => void;
  onToggleTerminal: () => void;
}

export function TelemetryTerminalPillar({
  isProbing,
  probeResult,
  showTelemetryTerminal,
  terminalLogs,
  onProbeGateway,
  onToggleTerminal,
}: TelemetryTerminalPillarProps) {
  return (
    <div className="lg:col-span-4 border border-stone-800 bg-stone-900/60 backdrop-blur-xl p-6 relative group overflow-hidden">
      {/* Corner Decorative Crosshairs */}
      <div className="absolute top-1 left-1 text-[10px] font-mono text-stone-600">⌖ 01</div>
      <div className="absolute top-1 right-1 text-[10px] font-mono text-stone-600">01 ⌖</div>

      <div className="flex items-center justify-between pb-4 border-b border-stone-800">
        <div className="flex items-center gap-2">
          <Terminal className="w-4 h-4 text-emerald-400" />
          <span className="text-xs font-mono uppercase tracking-widest text-stone-300">
            Node Telemetry
          </span>
        </div>
        <span className="text-[10px] font-mono px-2 py-0.5 bg-stone-800 text-stone-400">
          PROBE ACTIVE
        </span>
      </div>

      {/* Kinetic Oscilloscope / Signal Waveform */}
      <div className="my-5 p-3 bg-black/60 border border-stone-800/80">
        <div className="flex items-center justify-between text-[10px] font-mono text-stone-500 mb-1">
          <span>SIGNAL FREQ (401 Hz)</span>
          <span className="text-amber-400">UNAUTHORIZED</span>
        </div>
        <svg className="w-full h-12 stroke-amber-400 fill-none" viewBox="0 0 300 50">
          <path
            d="M 0 25 L 30 25 L 45 10 L 60 40 L 75 25 L 120 25 L 135 5 L 150 45 L 165 25 L 210 25 L 225 15 L 240 35 L 255 25 L 300 25"
            strokeWidth="1.5"
            className="animate-pulse"
          />
        </svg>
      </div>

      {/* Diagnostic Metrics Matrix */}
      <div className="space-y-2 text-xs font-mono text-stone-400">
        <div className="flex justify-between py-1 border-b border-stone-800/40">
          <span className="text-stone-500">REALM:</span>
          <span className="text-stone-200">silvestre.club</span>
        </div>
        <div className="flex justify-between py-1 border-b border-stone-800/40">
          <span className="text-stone-500">AUTH IDENTITY:</span>
          <span className="text-amber-400 font-semibold">ANONYMOUS (401)</span>
        </div>
        <div className="flex justify-between py-1 border-b border-stone-800/40">
          <span className="text-stone-500">CIPHER:</span>
          <span className="text-stone-200">RSA-256 JWT</span>
        </div>
        <div className="flex justify-between py-1 border-b border-stone-800/40">
          <span className="text-stone-500">RESTRICTION:</span>
          <span className="text-emerald-400">OPERATOR / ADMIN</span>
        </div>
      </div>

      {/* Interactive Live Probe Gateway Button */}
      <div className="mt-5 pt-4 border-t border-stone-800">
        <button
          type="button"
          onClick={onProbeGateway}
          disabled={isProbing}
          className="w-full py-2 px-3 bg-stone-800/80 hover:bg-stone-700/80 active:bg-stone-900 border border-stone-700 text-stone-300 hover:text-white text-xs font-mono flex items-center justify-center gap-2 transition"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isProbing ? "animate-spin text-amber-400" : ""}`} />
          <span>{isProbing ? "PROBING GATEWAY..." : "PING AUTH GATEWAY"}</span>
        </button>
        {probeResult && (
          <p className="mt-2 text-[11px] font-mono text-emerald-400/90 leading-tight">
            ✓ {probeResult}
          </p>
        )}
      </div>

      {/* Collapsible Telemetry Terminal Toggle */}
      <div className="mt-4">
        <button
          type="button"
          onClick={onToggleTerminal}
          className="text-[11px] font-mono text-stone-500 hover:text-stone-300 underline flex items-center gap-1"
        >
          <span>{showTelemetryTerminal ? "[-] Hide system log" : "[+] Inspect security log"}</span>
        </button>
        {showTelemetryTerminal && (
          <div className="mt-2 p-2 bg-black/90 border border-stone-800 font-mono text-[10px] text-stone-400 max-h-36 overflow-y-auto space-y-1">
            {terminalLogs.map((log, idx) => (
              <div key={idx} className="leading-snug">
                {log}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
